import React, {FC, SyntheticEvent, useMemo, useState} from "react";
import {useErrorCallback, useProfileData} from "../../../../hooks";
import {Identification, LegalEntityIdentification, UuidResult} from "../../../../api-client";
import {createIdentificationManager} from "../../../../di";
import IdentificationContext from "../../../context/IdentificationContext";
import {
  IdentificationBlockGroup, IdentificationConclusion,
  IdentificationPassport,
  IdentificationPersonal
} from "../blocks";
import {useCombinedData} from "../../../../hooks/identification/useCombinedData";
import moment from "moment";

interface Props {
    onSubmit: (uuid: UuidResult) => void;
    identification: Identification|undefined;

}

const IdentificationLegalEntity: FC<Props> = ({onSubmit, identification}) => {
  const {role} = useProfileData();
  const value = useCombinedData();
  const handleError = useErrorCallback();
  const [personalValid, setPersonalValid] = useState(false);
  const [passportValid, setPassportValid] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const dataValid = personalValid && passportValid;

  const identificationInput: LegalEntityIdentification = useMemo(() => {
    return {
      person: {
        firstName: value.firstName,
        middleName: value.middleName,
        lastName: value.lastName,
        snils: value.snils,
        position: value.jobPosition
      },
      inn: value.inn,
      email: value.email,
      company: {
        inn: value.innLegalEntity,
        name: value.companyName,
        kpp: value.companyKpp,
        address: value.companyAddress,
      },
      passport: {
        series: value.passportSeries,
        number: value.passportNumber,
        issuedDate: value.passportIssuedDate === null
          ? moment().format('YYYY-MM-DD')
          : moment(value.passportIssuedDate).format('YYYY-MM-DD'),
        issuedBy: value.passportIssuedBy,
        departmentCode: value.passportDepartmentCode,
        citizenship: value.citizenship,
        firstPassportScans: value.firstPassportScans,
        secondPassportScans: value.secondPassportScans,
        thirdPassportScans: value.thirdPassportScans,
        registrationAddress: value.registrationAddress,
        birthplace: value.birthplace,
        birthday: value.birthday === null
          ? moment().format('YYYY-MM-DD')
          : moment(value.birthday).format('YYYY-MM-DD'),
      }
    };
  }, [
    value.firstName,
    value.middleName,
    value.lastName,
    value.inn,
    value.email,
    value.snils,
    value.passportSeries,
    value.passportNumber,
    value.passportIssuedDate,
    value.passportIssuedBy,
    value.passportDepartmentCode,
    value.citizenship,
    value.firstPassportScans,
    value.secondPassportScans,
    value.thirdPassportScans,
    value.registrationAddress,
    value.birthplace,
    value.birthday,
    value.jobPosition,
    value.innLegalEntity,
    value.companyName,
    value.companyKpp,
    value.companyAddress,
  ]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      let result;
      const manager = await createIdentificationManager();
      result  = await manager.createLegalEntityIdentification(identificationInput, role);
      result && onSubmit(result);
    } catch (error: any) {
      await handleError(error);
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <IdentificationContext.Provider value={value}>
        <IdentificationBlockGroup>
          <IdentificationPersonal isLegalEntity={true} onValidated={(value: boolean) => setPersonalValid(value)} identification={identification} />
          <IdentificationPassport onValidated={(value: boolean) => setPassportValid(value)} passport={identification?.passport} />
          <IdentificationConclusion dataValid={dataValid} loading={loading}/>
        </IdentificationBlockGroup>
      </IdentificationContext.Provider>
    </form>
  );
};

export default IdentificationLegalEntity;